import { useFloraUserPreferences } from '../../../../api/agents-client/agent-client.hooks';
import type { ApprovalMode } from '../../../../api/agents-client/agent-client.type';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { resolveApprovalMode } from './flora-approval-mode-toggle.helpers';

interface FloraApprovalModeState {
  /** The mode the composer shows and the next turn runs with (sent as `approval_mode`). */
  mode: ApprovalMode;
  /** The chat's own composer choice, or null when it follows the default (sent as `chat_approval_mode`). */
  chatMode: ApprovalMode | null;
}

/**
 * Resolves the approval mode for one Flora chat, which the composer both
 * displays and sends.
 *
 * The chat's own choice comes from the per-thread map (or the draft for a new
 * chat with no thread id yet); without one the chat follows the user's saved
 * default, falling back to Ask. The toggle and the message payload read this
 * one source, so a loading or failed preferences fetch shows Ask and sends
 * Ask instead of showing Ask while the backend applies a saved Auto default.
 *
 * @param {string | null | undefined} threadId - The chat's thread id; empty for a new chat
 * @returns {FloraApprovalModeState | null} The resolved and per-chat modes; null when the organization does not
 *   have Auto mode enabled (the toggle is hidden and nothing is sent, so the backend forces Ask)
 *
 * @example
 * const approval = useFloraApprovalMode(threadId);
 * askAgent(socket, question, ..., approval?.mode, approval?.chatMode);
 */
function useFloraApprovalMode(threadId: string | null | undefined): FloraApprovalModeState | null {
  const isAutoModeEnabled = useFeatureFlag(FeatureFlag.FloraAutoMode);
  const chatMode = useGlobalStore((state) =>
    threadId ? (state.floraChatApprovalModes[threadId] ?? null) : state.floraDraftApprovalMode,
  );
  const { preferences } = useFloraUserPreferences();

  if (!isAutoModeEnabled) {
    return null;
  }

  return { mode: resolveApprovalMode(chatMode, preferences?.default_approval_mode), chatMode };
}

export { useFloraApprovalMode };
